import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { fetchDashboardDefinition } from 'src/services/aws/CloudWatchMetricsService';
import { extractSingleValueWidgets } from 'src/components/fintech-ops/shared/utils/cloudWatchDashboardMetrics';
import { logger } from 'src/logger';
import { DashboardNarrativeWidget, toNarrativeWidgets } from '../narrative/dashboardNarrativePrompt';

/**
 * Extracts the singleValue-widget numbers behind one CloudWatch dashboard for the
 * trailing one-month window (now − 1 month → now), ready to feed the AI narrative on
 * the KPI Metrics page.
 *
 * The reporting window is a rolling last-month range (v1): the embedded dashboard iframe
 * owns its own time-range picker but exposes neither a getter nor a change event and is
 * cross-origin, so the parent cannot read it — the narrative therefore states the window
 * it actually covers rather than trying to mirror the iframe.
 */
export interface UseDashboardNarrativeDataResult {
  /** Resolved singleValue widgets for the dashboard (empty if none / not yet loaded). */
  widgets: DashboardNarrativeWidget[];
  /** The reporting window label the numbers cover, e.g. "Aug 3, 2026 – Sep 3, 2026". */
  windowLabel: string;
  loading: boolean;
  error: Error | null;
}

export const useDashboardNarrativeData = (dashboardName: string): UseDashboardNarrativeDataResult => {
  const [widgets, setWidgets] = useState<DashboardNarrativeWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Trailing one-month window (now − 1 month → now). Computed once on mount; `windowLabel`
  // is the human-readable form shown in the narrative and used as part of its cache key.
  const { startTime, endTime, windowLabel } = useMemo(() => {
    const now = moment();
    const start = now.clone().subtract(1, 'month');
    return {
      startTime: start.toDate(),
      endTime: now.toDate(),
      windowLabel: `${start.format('MMM D, YYYY')} – ${now.format('MMM D, YYYY')}`
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setWidgets([]);

    const load = async () => {
      try {
        const body = await fetchDashboardDefinition(dashboardName);
        const groups = await extractSingleValueWidgets(body, startTime, endTime);
        if (!cancelled) setWidgets(toNarrativeWidgets(groups));
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        logger.error(`useDashboardNarrativeData: failed for ${dashboardName}`, e);
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dashboardName, startTime, endTime]);

  return { widgets, windowLabel, loading, error };
};
